import {isBrowserRuntime} from '../../runtime/browser';

/** Returns true when a client-credentials token may be minted for this call. */
export type ClientCredentialsGuard = () => Promise<boolean> | boolean;

// On globalThis, not a module variable: Next bundles instrumentation, route
// handlers and server actions separately, so a plain module-scope singleton
// registered at startup wouldn't be visible to the action's bundle. The token
// cache in this package uses globalThis for the same reason.
interface GuardGlobal {
  __zoeClientCredentialsGuard?: ClientCredentialsGuard;
}

/**
 * Lets a consuming app decide whether a browser-originated client-credentials
 * request may mint a token — e.g. a Vercel BotID check that passes a real
 * browser and rejects curl/Postman. Registered once at app startup.
 *
 * The app owns this so @zoefin/request stays free of Next/Vercel/BotID
 * dependencies; server-only consumers (lambdas) simply never register one.
 */
export function registerClientCredentialsGuard(
  guard: ClientCredentialsGuard,
): void {
  (globalThis as GuardGlobal).__zoeClientCredentialsGuard = guard;
}

/**
 * Enforces the registered guard, but only for a browser-originated Server
 * Action RPC — identified by the `next-action` request header Next sets on
 * those POSTs. In-process server calls (SSR, server components, lambdas) carry
 * no such header and are trusted: they aren't reachable by an external client.
 * A browser RPC with no guard registered fails closed.
 */
export async function isClientCredentialsRequestAllowed(): Promise<boolean> {
  // The action body always runs server-side; a browser runtime here means a
  // non-Next consumer with no request context, so there's nothing to gate.
  if (isBrowserRuntime()) return true;

  let actionHeader: string | null = null;

  try {
    const {headers} = await import('next/headers');

    actionHeader = (await headers()).get('next-action');
  } catch {
    // No Next request context (lambda, test runner) — a trusted server call.
    return true;
  }

  // No `next-action` header: an in-process SSR / server-component call.
  if (!actionHeader) return true;

  // Browser RPC: the app's guard decides, and an unregistered app fails closed.
  const guard = (globalThis as GuardGlobal).__zoeClientCredentialsGuard;

  if (!guard) return false;

  // A throwing guard denies rather than rejecting out of the token resolver.
  try {
    return await guard();
  } catch {
    return false;
  }
}
